import { ReactNode } from 'react';

import { Stack } from '@mui/material';
import clsx from 'clsx';

import { CloseDialogButton } from './CloseDialogButton';
import { tokens } from '../../../theme/generated/tokens';
import { CssVarDefinitions } from '../../../theme/types';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';
import { mergeStyles, cardVars, toDsVarsDefinition } from '../../variables';

const classNamesFactory = classNamesFor('DialogCard');

const classNames = {
  root: classNamesFactory.root(),
  innerCard: classNamesFactory.element('inner-card'),
};

const DEFAULT_SIZE = 560;

export type DialogCardProps<RESULT = unknown> = {
  /**
   * Size (width) of the dialog card, defaults to 560px
   */
  size?: number;
  /**
   * CSS Variable definitions to be applied to the `style` attribute of the dialog card
   */
  cssVars?: CssVarDefinitions;
  /**
   * Content, to be displayed above the dialog card
   */
  contentAbove?: ReactNode;
  /**
   * Content, to be displayed below the dialog card
   */
  contentBelow?: ReactNode;
  /**
   * Usage example: when dialog was opened from the drawer and we don't want to close the drawer, when user clicks on the dialog
   */
  stopClickPropagation?: boolean;
  /**
   * The result to emit when closing the dialog
   */
  hideCloseButton?: boolean;
  /**
   * Hides close button if true
   */
  result?: RESULT;
  children?: ReactNode;
  sx?: SxProps;
  className?: string;
  testId?: string;
};

const dsVars = toDsVarsDefinition({
  inset: {
    pt: '16px',
    pr: '24px',
    pb: '16px',
    pl: '24px',
  },
  divider: {
    color: tokens.theme.border.structural.neutral,
  },
  actionsGroup: {
    gap: '12px',
  },
});

/**
 * Base component for building dialog variants.
 * Gives the dialogs a card-like look and provides the CSS Variables for dialog building blocks (insets, divider, actions, etc.)
 */
export const DialogCard = <RESULT = unknown,>({
  size = DEFAULT_SIZE,
  testId = 'dialog',
  className,
  contentAbove,
  contentBelow,
  children,
  cssVars,
  sx,
  stopClickPropagation,
  result,
  hideCloseButton = false,
}: DialogCardProps<RESULT>) => {
  return (
    <Stack
      style={mergeStyles(dsVars, cssVars)}
      sx={mergeSx(
        {
          borderRadius: cardVars.refs.borderRadius,
          position: 'relative',
          maxHeight: '90vh',
          width: size,
          maxWidth: size,
        },
        sx
      )}
      onClick={
        stopClickPropagation
          ? (event) => {
              event.stopPropagation();
            }
          : undefined
      }
      className={clsx(classNames.root, className)}
    >
      <Stack
        sx={{
          width: size,
          maxWidth: size,
          borderRadius: cardVars.refs.borderRadius,
          boxShadow: tokens.theme.shadows.elevation.high,
        }}
        className={classNames.innerCard}
        data-testid={testId}
      >
        <Stack
          sx={{
            overflow: 'hidden',
            borderRadius: 'inherit',
            // Only apply the background color to the wrapper if there is no content above the dialog
            // This prevents the visual bug where the light background of the container
            // is visible behind the dark background of the stepper
            ...(!contentAbove && {
              backgroundColor: tokens.theme.bg.structural.surface,
            }),
          }}
        >
          {contentAbove}
          <Stack
            sx={{
              position: 'relative',
              ...(contentAbove && {
                backgroundColor: tokens.theme.bg.structural.surface,
              }),
            }}
          >
            {children}
            {!hideCloseButton && (
              <CloseDialogButton
                sx={{ position: 'absolute', top: 16, right: 16 }}
                result={result}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      {contentBelow}
    </Stack>
  );
};

DialogCard.classNames = classNames;
